import { animate, useInView } from "motion/react";
import { useEffect, useRef } from "react";

interface CountUpProps {
  to: number;
  from?: number;
  direction?: "up" | "down";
  delay?: number;
  duration?: number;
  className?: string;
  startWhen?: boolean;
  separator?: string;
  placeholder?: string;
  onStart?: () => void;
  onEnd?: () => void;
}

function getDecimalPlaces(num: number): number {
  const str = num.toString();
  if (!str.includes(".")) return 0;
  const decimals = str.split(".")[1];
  return parseInt(decimals, 10) !== 0 ? decimals.length : 0;
}

function formatCount({
  latest,
  maxDecimals,
  separator,
}: {
  latest: number;
  maxDecimals: number;
  separator: string;
}): string {
  const hasDecimals = maxDecimals > 0;
  const formattedNumber = Intl.NumberFormat("en-US", {
    useGrouping: !!separator,
    minimumFractionDigits: hasDecimals ? maxDecimals : 0,
    maximumFractionDigits: hasDecimals ? maxDecimals : 0,
  }).format(latest);

  return separator ? formattedNumber.replaceAll(",", separator) : formattedNumber;
}

export default function CountUp({
  to,
  from = 0,
  direction = "up",
  delay = 0,
  duration = 2,
  className = "",
  startWhen = true,
  separator = "",
  placeholder,
  onStart,
  onEnd,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const onStartRef = useRef(onStart);
  const onEndRef = useRef(onEnd);

  useEffect(() => {
    onStartRef.current = onStart;
    onEndRef.current = onEnd;
  }, [onStart, onEnd]);

  const isInView = useInView(ref, { once: true, margin: "0px" });
  const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));
  const startValue = direction === "down" ? to : from;
  const endValue = direction === "down" ? from : to;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (placeholder && !isInView) return;

    node.textContent = formatCount({ latest: startValue, maxDecimals, separator });
  }, [startValue, maxDecimals, separator, placeholder, isInView]);

  useEffect(() => {
    const node = ref.current;
    if (!node || !isInView || !startWhen) return;

    onStartRef.current?.();

    const controls = animate(startValue, endValue, {
      type: "spring",
      visualDuration: duration,
      bounce: 0,
      delay,
      onUpdate: (latest) => {
        node.textContent = formatCount({ latest, maxDecimals, separator });
      },
      onComplete: () => {
        onEndRef.current?.();
      },
    });

    return () => {
      controls.stop();
    };
  }, [isInView, startWhen, startValue, endValue, duration, delay, maxDecimals, separator]);

  return (
    <span className={className} ref={ref}>
      {placeholder}
    </span>
  );
}
